import { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, Building2, MapPin, Briefcase } from "lucide-react";
import { Link, useSearch } from "wouter";
import { useTranslations } from "@/lib/translations";
import { slugify } from "@/lib/slugify";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import companiesData from "@/data/companies.json";
import { usePageMeta } from "@/hooks/use-page-meta";
import { useJsonLd } from "@/hooks/use-json-ld";
import { buildBreadcrumbList } from "@/lib/breadcrumbs";

interface Company {
  Name: string;
  "Short Name": string;
  Isin: string;
  Country: string;
  Sector: string;
}

const companies: Company[] = companiesData as Company[];

const countries = Array.from(new Set(companies.map((c) => c.Country))).sort();
const sectors = Array.from(new Set(companies.map((c) => c.Sector))).sort();

const countryAr: Record<string, string> = {
  "Bahrain": "البحرين",
  "Jordan": "الأردن",
  "Kuwait": "الكويت",
  "Lebanon": "لبنان",
  "Oman": "عُمان",
  "Palestine": "فلسطين",
  "Qatar": "قطر",
  "Saudi Arabia": "المملكة العربية السعودية",
  "United Arab Emirates": "الإمارات العربية المتحدة",
};

const sectorAr: Record<string, string> = {
  "Banks": "البنوك",
  "Business Support Services": "خدمات دعم الأعمال",
  "Chemicals": "الكيماويات",
  "Construction & Engineering": "الإنشاء والهندسة",
  "Construction Materials": "مواد البناء",
  "Consumer Durables": "السلع المعمرة",
  "Containers & Packaging": "الحاويات والتعبئة",
  "Diversified Financial Services": "الخدمات المالية المتنوعة",
  "Education": "التعليم",
  "Electrical Components and Equipment": "المكونات والمعدات الكهربائية",
  "Energy": "الطاقة",
  "Food and Beverages": "الأغذية والمشروبات",
  "Health Care": "الرعاية الصحية",
  "Hotels and Leisure": "الفنادق والترفيه",
  "Industrial Conglomerates": "المجمعات الصناعية",
  "Insurance": "التأمين",
  "Media and Publishing": "الإعلام والنشر",
  "Metals and Mining": "المعادن والتعدين",
  "Paper & Forest Products": "منتجات الورق والغابات",
  "REITS": "صناديق الاستثمار العقاري",
  "Real Estate": "العقارات",
  "Retail": "التجزئة",
  "Software": "البرمجيات",
  "Telecommunication Services": "خدمات الاتصالات",
  "Tobacco": "التبغ",
  "Transportation and Logistics": "النقل واللوجستيات",
  "Travel and Tourism": "السفر والسياحة",
  "Utilities": "المرافق العامة",
};

const coverageMarkets = [
  { name: "Lebanon",      nameAr: "لبنان",                      count: 10,  x: 7,  y: 10 },
  { name: "Palestine",    nameAr: "فلسطين",                     count: 48,  x: 5,  y: 24 },
  { name: "Jordan",       nameAr: "الأردن",                     count: 78,  x: 17, y: 33 },
  { name: "Kuwait",       nameAr: "الكويت",                     count: 184, x: 60, y: 20 },
  { name: "Bahrain",      nameAr: "البحرين",                    count: 44,  x: 67, y: 38 },
  { name: "Qatar",        nameAr: "قطر",                        count: 54,  x: 71, y: 50 },
  { name: "Saudi Arabia", nameAr: "المملكة العربية السعودية",   count: 258, x: 40, y: 56 },
  { name: "UAE",          nameAr: "الإمارات",                   count: 179, x: 79, y: 61 },
  { name: "Oman",         nameAr: "عُمان",                      count: 28,  x: 87, y: 70 },
];

const Companies = () => {
  const { t, isRTL, language } = useTranslations();
  const isAr = language === "ar";
  const searchString = useSearch();
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [sectorFilter, setSectorFilter] = useState("all");
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const country = params.get("country");
    const sector = params.get("sector");
    setCountryFilter(country && countries.includes(country) ? country : "all");
    setSectorFilter(sector && sectors.includes(sector) ? sector : "all");
  }, [searchString]);

  usePageMeta({
    title: "ESG Ratings: 880+ Middle East Listed Companies | ESG Invest",
    description:
      "Browse ESG ratings for 880+ Middle East listed companies across 10 markets. Filter by country, sector & ESG score. Independent, sector-adjusted ratings.",
    path: "/companies",
  });

  useJsonLd(
    "ld-companies-breadcrumbs",
    buildBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "Companies", path: "/companies" },
    ]),
  );

  const filtered = useMemo(() => {
    return companies.filter((c) => {
      const matchesSearch =
        search === "" ||
        c.Name.toLowerCase().includes(search.toLowerCase()) ||
        c["Short Name"].toLowerCase().includes(search.toLowerCase());
      const matchesCountry =
        countryFilter === "all" || c.Country === countryFilter;
      const matchesSector =
        sectorFilter === "all" || c.Sector === sectorFilter;
      return matchesSearch && matchesCountry && matchesSector;
    });
  }, [search, countryFilter, sectorFilter]);

  return (
    <Layout>
      <section className="relative text-white py-20 overflow-hidden" style={{ backgroundImage: 'url(/subscriptions-hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-black/60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            <Building2 className="h-4 w-4 text-accent" />
            {t('companies.badge')}
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-center" data-testid="text-companies-title">
            {t("companies.title")}
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8" data-testid="text-companies-subtitle">
            {t("companies.subtitle")}
          </p>
          <p className="text-sm text-white/50 mb-3 tracking-wide uppercase">{t('companies.explore')}</p>
          <div className="flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
            {coverageMarkets
              .slice()
              .sort((a, b) => b.count - a.count)
              .map((market) => (
                <button
                  key={market.name}
                  onClick={() => setCountryFilter(market.name === "UAE" ? "United Arab Emirates" : market.name)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 transition-colors text-sm"
                  data-testid={`market-pill-${market.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                  <span className="font-medium text-white">{isAr ? market.nameAr : market.name}</span>
                  <span className="text-white/60 text-xs">{market.count}</span>
                </button>
              ))}
          </div>
        </div>
      </section>
      <div className="bg-se-gray-50 dark:bg-background py-12">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Card className="p-6 mb-8 border-0 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground ${isRTL ? "right-3" : "left-3"}`} />
                <Input
                  placeholder={t("companies.searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={`${isRTL ? "pr-10" : "pl-10"}`}
                  data-testid="input-search-company"
                />
              </div>
              <Select value={countryFilter} onValueChange={setCountryFilter}>
                <SelectTrigger data-testid="select-country-filter">
                  <SelectValue placeholder={t("companies.allCountries")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("companies.allCountries")}</SelectItem>
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sectorFilter} onValueChange={setSectorFilter}>
                <SelectTrigger data-testid="select-sector-filter">
                  <SelectValue placeholder={t("companies.allSectors")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("companies.allSectors")}</SelectItem>
                  {sectors.map((sector) => (
                    <SelectItem key={sector} value={sector}>
                      {sector}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground mt-4" data-testid="text-results-count">
              {t("companies.showing")} {filtered.length} {t("companies.of")} {companies.length} {t("companies.companies")}
            </p>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((company, idx) => (
              <Link key={`${company.Isin}-${idx}`} href={`/companies/${slugify(company.Name)}`}>
              <Card
                className="p-5 border-0 shadow-sm flex flex-col gap-2 cursor-pointer hover:shadow-md hover:border-accent/30 border border-transparent transition-all duration-150 group"
                data-testid={`card-company-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <div className="bg-accent/10 p-2 rounded-lg flex-shrink-0">
                    <Building2 className="h-5 w-5 text-accent" />
                  </div>
                  <div className="min-w-0" dir="ltr">
                    <h3 className="font-semibold text-foreground text-sm leading-tight" data-testid={`text-company-name-${idx}`}>
                      {company.Name}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{company["Short Name"]}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {isAr ? (countryAr[company.Country] ?? company.Country) : company.Country}
                  </span>
                  <span className="flex items-center gap-1">
                    <Briefcase className="h-3 w-3" />
                    {isAr ? (sectorAr[company.Sector] ?? company.Sector) : company.Sector}
                  </span>
                </div>
                <div className="border-t border-border mt-3 pt-3 space-y-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-muted-foreground">{isAr ? "درجة ESG" : "ESG Score"}</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-accent/15 text-accent blur-sm select-none">75%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    {[
                      { label: "E", value: "72%", color: "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400" },
                      { label: "S", value: "78%", color: "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400" },
                      { label: "G", value: "80%", color: "bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400" },
                    ].map(({ label, value, color }) => (
                      <div key={label} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${color}`}>
                        <span>{label}</span>
                        <span className="font-bold blur-sm select-none">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg" data-testid="text-no-results">
                {t("companies.noResults")}
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Companies;
